import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRightLeft, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const LABELS = {
  checkins: "Check-ins", journal_entries: "Journal entries", supporters: "Supporters", supporter_notes: "Supporter notes",
  custom_items: "Custom check-in items", voice_checkins: "Voice check-ins", skill_logs: "Coping skill logs", custom_emotions: "Custom emotions",
};
// Counts only: entry text is never shown to admins
const CollectionPreview = ({ name, c, includeTest }) => (
  <div className="mm-soft p-3 text-sm font-semibold flex justify-between" data-testid={`import-coll-${name}`}>
    <span>{LABELS[name]}</span>
    <span data-testid={`import-count-${name}`}>{includeTest ? c.total : c.total - c.test} to move{c.test ? ` · ${c.test} test` : ""}</span>
  </div>
);

export const ImportAccount = () => {
  const [email, setEmail] = useState("admin@mindfulme.app");
  const [data, setData] = useState(null);
  const [includeTest, setIncludeTest] = useState(false);
  const [busy, setBusy] = useState("");
  const [moved, setMoved] = useState(null);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const nonEmpty = useMemo(() => (data ? Object.entries(data.collections).filter(([, c]) => c.total) : []), [data]);

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const preview = () => run("preview", async () => { setMoved(null); setCode(""); setCodeSent(false); setData((await api.get("/admin/import/preview", { params: { source_email: email } })).data); });
  const doImport = () => run("import", async () => {
    const r = (await api.post("/admin/import", { source_email: email, include_test: includeTest, code })).data;
    setMoved(r.total); toast.success(`Moved ${r.total} items into your account`);
    setData((await api.get("/admin/import/preview", { params: { source_email: email } })).data);
  });
  const sendCode = () => run("code", async () => { await api.post("/admin/import/send-code", { source_email: email }); setCodeSent(true); toast.success(`Code sent to ${email}`); });
  const needsCode = data && !data.same_name;
  const retire = () => {
    if (!window.confirm(`Delete the ${email} profile and anything left in it? This can't be undone.`)) return;
    run("retire", async () => { await api.post("/admin/import/retire", { source_email: email }); toast.success(`${email} deleted`); setData(null); });
  };

  return (
    <div className="mm-card p-5 mb-6" data-testid="import-account-card">
      <h3 className="font-semibold flex items-center gap-2"><ArrowRightLeft size={18} />Import data from another account</h3>
      <p className="text-sm text-mm-muted mt-1">Moves that account's records into yours. Nothing is deleted — it's just re-linked.</p>
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <input className="mm-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="import-source-email" />
        <button className="mm-btn-ghost" onClick={preview} disabled={!!busy} data-testid="import-preview-button">{busy === "preview" ? <Loader2 size={16} className="animate-spin" /> : "Preview"}</button>
      </div>
      {data && (
        <div className="mt-4 space-y-2" data-testid="import-preview">
          {nonEmpty.map(([k, c]) => <CollectionPreview key={k} name={k} c={c} includeTest={includeTest} />)}
          {!nonEmpty.length && <p className="text-sm text-mm-muted" data-testid="import-nothing">Nothing left in this account.</p>}
          <label className="flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="import-include-test" />Include test-looking items</label>
          {moved !== null && <p className="text-sm text-mm-success font-semibold" data-testid="import-result">Moved {moved} items into your account.</p>}
          {needsCode && nonEmpty.length > 0 && (
            <div className="mm-soft p-3 text-sm space-y-2" data-testid="import-verify">
              <p>To protect members' privacy, confirm this account is yours: we'll email a 6-digit code to <b>{data.source_email}</b>.</p>
              <div className="flex flex-wrap gap-2">
                <button className="mm-btn-ghost !py-2 !px-3 text-sm" onClick={sendCode} disabled={!!busy} data-testid="import-send-code">{busy === "code" ? <Loader2 size={14} className="animate-spin" /> : codeSent ? "Resend code" : "Send code"}</button>
                {codeSent && <input className="mm-input !w-32 tracking-widest" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="import-code-input" />}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <button className="mm-btn" onClick={doImport} disabled={!!busy || (needsCode && code.length !== 6)} data-testid="import-run-button">{busy === "import" ? <Loader2 size={16} className="animate-spin" /> : "Move into my account"}</button>
            <button className="mm-btn-ghost text-mm-error" onClick={retire} disabled={!!busy} data-testid="import-retire-button"><Trash2 size={16} />Delete this profile</button>
          </div>
        </div>
      )}
    </div>
  );
};
